/**
 * Reduces inbound AG-UI events into epi-chat state mutations, routing each
 * event to the session_id it was tagged with on the backend. The routing
 * keeps a background session's welcome stream (or any concurrent agent run)
 * flowing into *that* session's in-memory state even while the user has
 * focused a different tab — when they swap back, everything they missed is
 * already there.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, type MutableRefObject } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import type { AgUiEvent } from '@verantos-dev/feature-agent-chat';
import { analysisKeys, markAnalysisProcessing } from '@verantos-dev/feature-analysis';

import type { RunCompletionNotifier } from './useRunCompletionNotifier';
import { parseEchoedAttachments } from '../api/attachmentWire';
import { CHECKPOINT_PAYLOAD_PARSERS, parseConceptMappings } from '../api/checkpointPayloadParsers';
import { memoryKeys } from '../api/memoryKeys';
import { ReadyAckSchema } from '../api/readyAckWire';
import {
  ResourceCardPayloadSchema,
  ResourceUpdatedPayloadSchema,
  UploadedFilePayloadSchema,
} from '../api/resourceEventWire';
import {
  isAvDetailQueryForVariable,
  isAvLibraryListQuery,
  resourceInvalidationKeys,
  type ResourceInvalidationResolvers,
} from '../api/resourceQueryKeys';
import { WireScopeSchema } from '../api/scopeWire';
import {
  epiChatActions,
  epiChatReducer,
  initialEpiChatState,
  type EpiChatAction,
  type EpiChatState,
} from '../state/epiChatSlice';
import type {
  AvSelectionCandidate,
  AvSelectionCheckpoint,
  EpiArtifact,
  EpiAttritionStep,
  EpiCheckpoint,
  EpiCheckpointStage,
  EpiClarifyingQuestion,
  EpiConceptBinding,
  EpiConceptGroupCode,
  EpiConceptGroupDefinition,
  EpiConceptGroupUsage,
  EpiExecutionConceptUsage,
  EpiExecutionCoverage,
  EpiExecutionDistribution,
  EpiExecutionTechnicalChecks,
  EpiMessage,
  EpiVariableFamily,
  EpiVariableFamilyMapping,
  EpiVariableFamilyMember,
} from '../types/epi-chat-types';
import { friendlyToolLabel } from '../utils/friendlyToolLabel';

const CHECKPOINT_STAGES: readonly EpiCheckpointStage[] = [
  'intent',
  'bindings',
  'draft',
  'validation',
  'judge',
  'cohort_plan',
  // OR-6271: the concept-group create-consent gate. This is the RUNTIME allowlist
  // the stream reducer checks (line ~633) — a stage missing here is silently
  // dropped, so the checkpoint part never reaches the UI and the gate is
  // invisible. Adding it to the ``EpiCheckpointStage`` *type* alone is not enough
  // (TS won't flag an incomplete array against a union), which is exactly why the
  // consent card never rendered.
  'concept_group',
  'process_analysis',
];

export type EpiChatDispatchTo = (sessionId: string, action: EpiChatAction) => void;

interface IncomingEvent {
  type: string;
  runId?: string;
  /** OR-5964 envelope: which agent emitted this event. */
  agentId?: string;
  /** OR-5964 envelope: parent tool_use_id for sub-agent emissions. */
  parentEventId?: string;
  /** OR-5964 envelope: 3-surface routing classification. */
  surface?: 'main' | 'drill' | 'pinned';
  payload?: Record<string, unknown>;
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

// TODO: cleanup with research_assistant_criterion_redundancy_guard ff
const asStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === 'string')
    ? (value as string[])
    : undefined;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

/**
 * OR-6504: coerce the variable-family block off a checkpoint envelope.
 *
 * Returns ``undefined`` unless the block is usable — a family whose generalized
 * definition or member list is missing must fall back to the ordinary copy
 * rather than render a card with blanks where the researcher expects the
 * definition they are approving.
 */
const asVariableFamily = (value: unknown): EpiVariableFamily | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const generalizedDescription =
    asString(raw.generalized_description) ?? asString(raw.generalizedDescription);
  const rawMembers = raw.members;
  if (!generalizedDescription || !Array.isArray(rawMembers) || rawMembers.length === 0) {
    return undefined;
  }
  const members: EpiVariableFamilyMember[] = [];
  for (const entry of rawMembers) {
    if (!entry || typeof entry !== 'object') continue;
    const member = entry as Record<string, unknown>;
    const parameterValue = asString(member.parameter_value) ?? asString(member.parameterValue);
    if (!parameterValue) continue;
    const rawWindow = member.window;
    let window: EpiVariableFamilyMember['window'];
    if (rawWindow && typeof rawWindow === 'object') {
      const w = rawWindow as Record<string, unknown>;
      const value = typeof w.value === 'number' ? w.value : Number(w.value);
      const unit = asString(w.unit);
      // A window without a usable value/unit is dropped rather than rendered as
      // "NaN units": the parameter value the model wrote still reads correctly on
      // its own.
      if (Number.isFinite(value) && value > 0 && unit) {
        window = { value, unit, direction: asString(w.direction) };
      }
    }
    members.push({
      parameterValue,
      displayName: asString(member.display_name) ?? asString(member.displayName) ?? '',
      alias: asString(member.alias) ?? '',
      isExemplar: (member.is_exemplar ?? member.isExemplar) === true,
      ...(window ? { window } : {}),
    });
  }
  if (members.length === 0) return undefined;
  const rawMappings = raw.concept_mappings ?? raw.conceptMappings;
  const conceptMappings: EpiVariableFamilyMapping[] = [];
  if (Array.isArray(rawMappings)) {
    for (const entry of rawMappings) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const clinicalTerm = asString(row.clinical_term) ?? asString(row.clinicalTerm);
      if (!clinicalTerm) continue;
      const rawGroups = row.concept_groups ?? row.conceptGroups;
      const conceptGroups: { conceptGroupId: string; name: string }[] = [];
      if (Array.isArray(rawGroups)) {
        for (const group of rawGroups) {
          if (!group || typeof group !== 'object') continue;
          const g = group as Record<string, unknown>;
          const name = asString(g.name);
          if (!name) continue;
          conceptGroups.push({
            conceptGroupId: asString(g.concept_group_id) ?? asString(g.conceptGroupId) ?? '',
            name,
          });
        }
      }
      const rawUsedIn = row.used_in ?? row.usedIn;
      const usedIn = Array.isArray(rawUsedIn)
        ? rawUsedIn.filter((n): n is number => typeof n === 'number' && n > 0)
        : [];
      conceptMappings.push({
        clinicalTerm,
        conceptGroups,
        usedIn,
        shared: (row.shared ?? row.isShared) === true,
      });
    }
  }
  const rawCount = raw.member_count ?? raw.memberCount;
  return {
    familyId: asString(raw.family_id) ?? asString(raw.familyId) ?? '',
    // The group's own title. Falls back to the generalized definition, matching
    // the backend's own fallback for specs written before the field existed —
    // never a blank heading.
    displayName: asString(raw.display_name) ?? asString(raw.displayName) ?? generalizedDescription,
    // Recovered from the members' aliases, and absent whenever it would not be
    // worth showing (including after a collision rename) — so a missing pattern
    // is a normal state, not a degraded one.
    aliasPattern: asString(raw.alias_pattern) ?? asString(raw.aliasPattern),
    parameterLabel: asString(raw.parameter_label) ?? asString(raw.parameterLabel) ?? '',
    generalizedDescription,
    outputType: asString(raw.output_type) ?? asString(raw.outputType),
    outputValues: asString(raw.output_values) ?? asString(raw.outputValues),
    conceptMappings,
    // The heading always shows the TRUE total, even though the card lists only
    // the first few — a count derived from the rendered subset would misreport
    // how many variables the researcher is approving.
    memberCount: typeof rawCount === 'number' && rawCount > 0 ? rawCount : members.length,
    members,
  };
};

/**
 * Coerce a wire-format attrition list into ``EpiAttritionStep[]``. The
 * backend serialises each step with snake_case-ish field names from the
 * Pydantic model — already camelCase-aligned (``name``, ``description``,
 * ``subjects``) — but a corrupt / partial entry must not crash the
 * reducer, so we filter to well-shaped rows. Returns ``undefined`` when
 * the input isn't an array (so the reducer's "leave existing value
 * alone" semantics fire) and ``[]`` when it's an empty list.
 */
const parseAttritionPayload = (value: unknown): EpiAttritionStep[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter(
      (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null
    )
    .map((entry) => ({
      name: asString(entry.name) ?? '',
      description: asString(entry.description) ?? '',
      subjects: typeof entry.subjects === 'number' ? entry.subjects : 0,
    }))
    .filter((step) => step.name);
};

/**
 * Strands streams a tool's `input` as a partial JSON *string* on each
 * `current_tool_use` event (e.g. `""` → `"{"` → `"{\"query\":"` → `"{\"query\":\"asthma\"}"`).
 * Once the string parses as a JSON object the tool-call is ready to render.
 * Accepts either an already-parsed record or a JSON string; returns `{}` when
 * the streaming JSON is still partial.
 */
const coerceArgs = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Partial JSON — haven't assembled a complete object yet.
    }
  }
  return {};
};

export const useEpiChatStream = (
  dispatchTo: EpiChatDispatchTo,
  activeSessionIdRef: MutableRefObject<string | null>,
  /**
   * OR-6089: per-kind query-key resolvers for `RESOURCE_UPDATED`
   * invalidation. Cohort + concept-group keys are resolved inline;
   * cross-feature kinds (analytical_variable) need their resolver
   * injected here to avoid pulling `feature-analytical-variables` into
   * feature-epi-chat's import graph (would close a build cycle since
   * `feature-analytical-variables` already imports types from us).
   *
   * Callers that don't supply a resolver get a no-op for that kind —
   * the parse + log path still runs, and other kinds invalidate as
   * usual. The project-page chat mount wires `avKeys` here; the
   * legacy global-RA mount omits it (no analytical variable navigation surface there).
   */
  resourceResolvers: ResourceInvalidationResolvers = {},
  /** Live run lifecycle hooks for the completion alert; omitted by callers that don't alert. */
  runLifecycle?: Pick<
    RunCompletionNotifier,
    'noteRunStarted' | 'noteRunCancelled' | 'noteRunFinished'
  >
) => {
  const queryClient = useQueryClient();
  const scopedAvInvalidation = Boolean(useFeatureFlagSplit('scoped_av_card_invalidation'));
  // Highest seq already processed per session. The durable multi-pod tailer (and
  // brief replay-overlap windows) can deliver the same event twice; we drop any
  // event at or below the last seq we've handled so it never double-renders.
  // Survives re-renders (ref) but not a full remount — which is fine: a remount
  // re-hydrates from the snapshot, not from re-dispatched seq'd events.
  const seenSeqRef = useRef<Map<string, number>>(new Map());
  // OR-6018 batch-hydrate replay. A full reload streams the durable ui# stream
  // back in ``session_replay`` chunks; we BUFFER those events per session
  // instead of dispatching them live (which re-animated the whole session and
  // flipped run-state into a spurious inflight). On ``replayComplete`` the
  // buffer is FOLDED — reduced through this same handler against a private
  // accumulator state — and applied as a SINGLE ``hydrated``. One apply, no
  // animation, no inflight flip, and re-delivery can't double the transcript.
  const replayBufferRef = useRef<Map<string, (AgUiEvent | IncomingEvent)[]>>(new Map());
  // Set only while folding a buffered replay. When present, ``dispatch`` routes
  // actions into ``foldStateRef`` via the reducer (no live ``dispatchTo``, so
  // the UI never sees the intermediate frames) and side effects (TanStack
  // invalidations, cohort-poll kickoff) are suppressed — a historical
  // RESOURCE_UPDATED must not re-fire a live poll on reload.
  const foldSessionRef = useRef<string | null>(null);
  const foldStateRef = useRef<EpiChatState>(initialEpiChatState);
  // Self-ref so the fold can re-enter this handler per buffered event, reusing
  // the EXACT live wire→action translation (no duplicated event→state logic).
  const handlerRef = useRef<((event: AgUiEvent | IncomingEvent) => void) | null>(null);
  const handleEvent = useCallback(
    (event: AgUiEvent | IncomingEvent) => {
      const runId = event.runId ?? 'unknown';
      const payload = asRecord(event.payload);
      // Every event the backend emits is tagged with `sessionId` on its
      // payload; fall back to the active session only for events that
      // predate subscription (e.g. CONNECTION_ESTABLISHED).
      const sessionId = asString(payload.sessionId) ?? activeSessionIdRef.current;
      if (!sessionId) return;
      // While folding a replay buffer, ``dispatch`` reduces into the private
      // accumulator and ``isFolding`` gates side effects to state-only.
      const isFolding = foldSessionRef.current !== null;
      const dispatch = (action: EpiChatAction) => {
        if (isFolding) {
          foldStateRef.current = epiChatReducer(foldStateRef.current, action);
          return;
        }
        dispatchTo(sessionId, action);
      };

      // Bump the per-session resume cursor. Every server-origin event carries
      // an ``eventSeq`` so the client can persist it and ask the server to
      // replay on the next subscribe.
      const seq = typeof payload.eventSeq === 'number' ? payload.eventSeq : undefined;
      if (seq !== undefined && !isFolding) {
        // Live de-dup: drop an event we've already processed for this session
        // (re-delivered by the durable tailer or a replay-overlap window) so it
        // doesn't render twice. New events (seq > seen) and resume-gap replays
        // (seq > the cursor we sent) still pass; hydration is a snapshot action,
        // not seq'd-event replay, so a full reload is never dropped here. During
        // a fold we deliberately skip this — buffered events are reduced into a
        // throwaway accumulator, and the cursor is seeded to the durable head
        // once, after the single hydrate (so re-delivery is dropped post-fold).
        const seen = seenSeqRef.current.get(sessionId) ?? 0;
        if (seq <= seen) return;
        seenSeqRef.current.set(sessionId, seq);
        dispatch(epiChatActions.eventSeqObserved({ seq }));
      }

      switch (event.type) {
        case 'RUN_STARTED':
          dispatch(epiChatActions.runStarted({ runId }));
          if (!isFolding) runLifecycle?.noteRunStarted(sessionId);
          return;
        case 'RUN_FINISHED':
          dispatch(epiChatActions.runFinished());
          if (!isFolding) runLifecycle?.noteRunFinished(sessionId);
          // Refresh the memory panel after every agent turn. The agent
          // can mutate use_count / last_used_at via cite_memory without
          // going through any FE mutation hook, so without this the
          // panel keeps showing the cached pre-turn values until the
          // user manually refreshes or fires another sidecar action.
          // Suppressed during a replay fold — a historical RUN_FINISHED
          // shouldn't trigger a refetch; the fold only rebuilds transcript
          // state. ``runFinished`` is still folded (it closes streaming
          // artifacts / pending executions).
          if (!isFolding) queryClient.invalidateQueries({ queryKey: memoryKeys.all });
          return;
        case 'ERROR': {
          const errorCode = asString(payload.code);
          const errorMessage = asString(payload.message) ?? 'Agent run failed.';
          // OR-6463: suppress a sub-agent error from propagating to the top-level banner,
          // since the run retries and succeeds. `surface` is a top-level AgUiEvent field,
          // not part of `payload` — sub-agent emissions default to 'drill' (OR-5964).
          if (!errorCode && event.surface === 'drill') {
            return;
          }
          // Soft-busy codes mean "your message wasn't accepted because a
          // previous run is still going on the server" — not "the run
          // crashed". Don't tear down our run state; route the message
          // into the busy-notice path so the Stop button stays visible
          // and the user can cancel the wedged run.
          if (errorCode === 'RUN_LEASE_HELD_ELSEWHERE' || errorCode === 'RUN_IN_PROGRESS') {
            dispatch(epiChatActions.runBusyNoticeReceived({ message: errorMessage }));
            return;
          }
          // A *retryable* subscribe refusal (transient/infra read failure on the
          // server) — back off and re-subscribe instead of dead-ending on the
          // terminal "no access" panel. Genuine FORBIDDEN still falls through.
          // OR-6348: also reset the optimistic send sentinel if it's the one
          // showing — a send that raced a still-recovering subscribe leaves
          // "RA is working" with no real RUN_STARTED coming to supersede it;
          // the re-subscribe won't re-emit the lost user_message. No-op unless
          // the sentinel owns runState (guarded in the reducer).
          if (errorCode === 'SUBSCRIBE_UNAVAILABLE') {
            dispatch(epiChatActions.optimisticSendRejected());
            dispatch(epiChatActions.subscribeRetryRequested());
            return;
          }
          // A ``user_message`` reached the server before the subscribe ack
          // (e.g. a cached pending card clicked during the refresh subscribe
          // window — the primary defense is the ``!subscribeAcked`` disabled
          // gate in the layouts, this is belt-and-suspenders for any racing
          // emit). Swallow it: the FE re-subscribes and the message is
          // recoverable, so a red run-crashed banner would be misleading.
          // OR-6348: still reset the optimistic send sentinel if it's the
          // one showing — otherwise "RA is working" would be stuck forever
          // with no real RUN_STARTED ever coming to supersede it.
          if (errorCode === 'NOT_SUBSCRIBED') {
            dispatch(epiChatActions.optimisticSendRejected());
            return;
          }
          // An unrecoverable tool failure resolves the working-state row, not a
          // top-level banner: render it as an inline `error` notice right after
          // the row (the run still ends normally via RUN_FINISHED). Genuine
          // run-level crashes (EMPTY_AGENT_RESPONSE, AGENT_RUN_ERROR) keep the
          // banner (OR-6151 / RA response structure).
          if (errorCode === 'TURN_FAILED_NO_RESOLUTION') {
            dispatch(epiChatActions.noticeReceived({ message: errorMessage, severity: 'error' }));
            return;
          }
          dispatch(epiChatActions.runErrored({ message: errorMessage }));
          return;
        }
        case 'TEXT_MESSAGE_START':
          // no-op; a streaming assistant message is created lazily on first content chunk
          return;
        case 'TEXT_MESSAGE_CONTENT': {
          const content = asString(payload.content);
          if (!content) return;
          // OR-5964 envelope routing — same shape as STATUS_UPDATE:
          //   - With a `parentEventId` (sub-agent text — Strands fires this
          //     for both the sub-agent's narrative reasoning and its final
          //     reply text) → append to that parent tool's internalLog so
          //     the drill-down renders it interleaved with the sub-agent's
          //     tool calls. Never lands on the orchestrator's assistant
          //     bubble — that's where chat-level prose belongs, not
          //     drill-down content.
          //   - Without a parent (orchestrator's own assistant text) →
          //     stream into the assistant message via textChunkReceived.
          // The reducer's subAgentReasoningReceived coalesces consecutive
          // chunks into one log entry, so a streamed reply doesn't
          // fragment into one row per token.
          const parentToolId = event.parentEventId;
          if (parentToolId) {
            dispatch(
              epiChatActions.subAgentReasoningReceived({
                parentToolId,
                text: content,
                agentId: event.agentId,
              })
            );
          } else {
            dispatch(epiChatActions.textChunkReceived({ runId, content }));
          }
          return;
        }
        case 'TEXT_MESSAGE_END':
          return;
        case 'TOOL_CALL_START': {
          const toolName =
            asString(payload.toolName) ??
            asString(payload.name) ??
            asString(payload.tool) ??
            'unknown_tool';
          const toolId = asString(payload.toolId) ?? asString(payload.toolUseId) ?? toolName;
          // Strands streams `input` as a JSON string that accumulates over
          // many `current_tool_use` events. `coerceArgs` parses it once the
          // string is a complete object; until then returns `{}` (the card
          // still renders with a generic label and upgrades live when args
          // parse successfully).
          const initialArgs = coerceArgs(payload.input ?? payload.args);
          // OR-5964 envelope: sub-agent tool calls carry `parentEventId`
          // (the orchestrator tool_use_id this nests under) + `agentId` (which
          // sub-agent emitted) on the AgUiEvent itself. The reducer attaches
          // tool calls with `parentEventId` to that parent's `subCalls`.
          const parentToolId = event.parentEventId;
          const agentId = event.agentId;
          dispatch(
            epiChatActions.toolCallStarted({
              runId,
              toolId,
              toolName,
              friendlyLabel: friendlyToolLabel(toolName),
              initialArgs: Object.keys(initialArgs).length > 0 ? initialArgs : undefined,
              parentToolId,
              agentId,
              surface: event.surface,
            })
          );
          // Auto tab-switch removed deliberately: yanking the user from
          // (say) the SQL tab to the Verification tab while they were
          // reading is jarring. The user is the only one who navigates
          // the drawer; tools fire in the background, the audit log /
          // artifact-ref card provides the "something happened" signal,
          // and the user clicks to look at it on their own time. The
          // TOOL_TAB_MAP table is left in the slice for now in case we
          // want to resurface it as an opt-in later, but it's no longer
          // dispatched here.
          void toolName;
          return;
        }
        case 'TOOL_CALL_ARGS': {
          const toolId = asString(payload.toolId) ?? asString(payload.toolUseId);
          if (!toolId) return;
          // Strands streams args in both `payload.input` (accumulated JSON
          // string) and `payload.currentToolUse.input`. Try both; coerceArgs
          // returns {} when the string is still partial.
          const nested = coerceArgs(asRecord(payload.currentToolUse).input);
          const direct = coerceArgs(payload.args ?? payload.input);
          const merged = { ...nested, ...direct };
          if (Object.keys(merged).length === 0) return;
          dispatch(
            epiChatActions.toolCallArgs({
              runId,
              toolId,
              args: merged,
            })
          );
          return;
        }
        case 'TOOL_CALL_END': {
          const toolId = asString(payload.toolId) ?? asString(payload.toolUseId);
          if (!toolId) return;
          dispatch(
            epiChatActions.toolCallEnded({
              runId,
              toolId,
              result: payload.result,
              error: asString(payload.error),
            })
          );
          return;
        }
        case 'INPUT_REQUEST': {
          const question: EpiClarifyingQuestion = {
            questionId: asString(payload.questionId) ?? `q-${runId}`,
            question: asString(payload.question) ?? '',
            options: (Array.isArray(payload.options)
              ? (payload.options as Record<string, unknown>[])
              : []
            ).map((opt) => ({
              label: asString(opt.label) ?? '',
              description: asString(opt.description),
              value: asString(opt.value),
              conceptGroupId:
                asString(opt.concept_group_id) ?? asString(opt.conceptGroupId) ?? null,
            })),
            allowCustom: payload.allowCustom !== false,
            answered: false,
          };
          dispatch(epiChatActions.clarifyingQuestionReceived({ runId, question }));
          return;
        }
        case 'STATUS_UPDATE': {
          // Welcome-stream bracket events: the backend emits these around the
          // one-shot welcome agent so the composer can be disabled until the
          // greeting is fully rendered.
          if (asString(payload.kind) === 'welcome') {
            const welcomeStatus = asString(payload.status);
            if (welcomeStatus === 'streaming') {
              dispatch(epiChatActions.initializingChanged({ initializing: true }));
            } else if (welcomeStatus === 'complete') {
              dispatch(epiChatActions.initializingChanged({ initializing: false }));
            }
            return;
          }
          // Multi-tab fan-out: the server echoes the sender's user message to
          // every tab in the session room so observers render the bubble live
          // (the sender already has it from the optimistic ``userMessageSent``).
          // Deduped by the client-minted id in the reducer, so the sender's own
          // echo and any buffered-resume replay are no-ops.
          if (asString(payload.kind) === 'user_message') {
            const messageId = asString(payload.messageId);
            const messageText = asString(payload.text);
            // A gate reply carries the id of the gate it resolved: a checkpoint
            // approval / AV selection carries ``checkpointId``; a clarifying-
            // question answer carries ``questionId`` (OR-6341). On a full reload
            // or a second tab this echo is the only thing that flips the gate
            // card into its resolved state (the live answer's ``userMessageSent``
            // doesn't re-run on replay); the durable ui# event persists the id
            // server-side, so replay folds it here.
            const questionId = asString(payload.questionId);
            const checkpointId = asString(payload.checkpointId);
            // An attachment-only turn has no text, so text alone can't gate the
            // echo that rebuilds the bubble.
            const attachments = parseEchoedAttachments(payload.attachments);
            if (messageId && (messageText || attachments.length)) {
              dispatch(
                epiChatActions.userMessageEchoed({
                  id: messageId,
                  text: messageText ?? '',
                  ...(questionId ? { questionId } : {}),
                  ...(checkpointId ? { checkpointId } : {}),
                  ...(attachments.length ? { attachments } : {}),
                })
              );
            }
            return;
          }
          // Re-announcement of an in-flight orchestrator run on resume. The
          // backend fires this from `on_subscribe` when the per-session task
          // is still alive — the client's resume cursor is usually past the
          // original RUN_STARTED, so without this hint runState would stay
          // 'idle' and the composer would show Send while tokens are still
          // streaming. We mirror RUN_STARTED so the cancel path keeps the
          // right run_id.
          if (asString(payload.kind) === 'run') {
            const runStatus = asString(payload.status);
            if (runStatus === 'streaming') {
              const resumedRunId = asString(payload.runId) ?? runId;
              dispatch(epiChatActions.runStarted({ runId: resumedRunId }));
            } else if (runStatus === 'idle') {
              // Definitive "no run in flight" ack from the backend. The
              // common trigger is server crash + restart: the namespace
              // registry rebuilt empty but the client still has tool_call
              // parts marked 'running' from before the crash. Close them
              // out as 'error' so spinners stop waiting for events that
              // will never arrive. Same reducer the resume-gap path
              // uses — closing-out semantics are identical.
              dispatch(epiChatActions.staleRunCleared());
            }
            return;
          }
          if (asString(payload.kind) === 'run_interrupted') {
            // Multi-pod lifecycle (docs/DESIGN_MULTIPOD_RUN_LIFECYCLE.md D4):
            // on a cold reconnect the backend found the run's pod dead (lease
            // expired) — there's no live task to tail. Unlike ``kind:'run'
            // status:'streaming'`` (run genuinely executing elsewhere, keep
            // streaming), this is terminal: record it so the transcript shows
            // a Retry card and the frozen "Agent is working…" clears.
            const interruptedRunId = asString(payload.runId) ?? runId;
            dispatch(
              epiChatActions.runInterrupted({
                runId: interruptedRunId,
                retryable: payload.retryable !== false,
              })
            );
            return;
          }
          if (asString(payload.kind) === 'session_replay') {
            const replaySessionId = asString(payload.sessionId);
            if (!replaySessionId) return;
            // OR-6018 batch-hydrate: the backend replays the durable ui# event
            // stream in ordered chunks. We BUFFER the chunks (no live dispatch —
            // that re-animated the whole session and flipped run-state into a
            // spurious inflight) and, on the LAST chunk (``replayComplete``),
            // fold the entire buffer into ONE final state and apply a single
            // ``hydrated``. The fold reuses THIS handler's exact wire→action
            // translation against a private accumulator (``foldStateRef``), so
            // there is no duplicated event→state logic and the reloaded render
            // is identical to live — minus the animation.
            const replayEvents = payload.events;
            if (Array.isArray(replayEvents)) {
              // First chunk (``replayReset``) opens a fresh buffer; later chunks
              // append in order. A long session arrives across many chunks.
              if (payload.replayReset !== false) {
                replayBufferRef.current.set(replaySessionId, []);
              }
              const buffer =
                replayBufferRef.current.get(replaySessionId) ??
                (() => {
                  // Defensive: a non-reset chunk with no open buffer (lost first
                  // chunk) — start one so we don't silently drop the replay.
                  const fresh: (AgUiEvent | IncomingEvent)[] = [];
                  replayBufferRef.current.set(replaySessionId, fresh);
                  return fresh;
                })();
              buffer.push(...(replayEvents as (AgUiEvent | IncomingEvent)[]));
              if (payload.replayComplete !== true) {
                // More chunks coming; keep showing the loading affordance
                // (``subscribing`` stays true until the final ``hydrated``).
                return;
              }
              // LAST chunk: fold the buffered events into one final transcript.
              // Reduce them through this handler in fold-mode (state-only, no
              // side effects, no live dispatch) starting from a clean session.
              replayBufferRef.current.delete(replaySessionId);
              foldSessionRef.current = replaySessionId;
              foldStateRef.current = initialEpiChatState;
              try {
                for (const replayed of buffer) {
                  handlerRef.current?.(replayed);
                }
              } finally {
                foldSessionRef.current = null;
              }
              const folded = foldStateRef.current;
              foldStateRef.current = initialEpiChatState;
              // The durable head carried on the complete marker. It can exceed
              // the max replayable ``eventSeq`` (trailing control-only rows still
              // advance the persisted seq), so seed BOTH the live de-dup cursor
              // and the resume cursor from it: any re-delivery of an already-
              // folded event (reconnect / tailer) is then dropped by live de-dup
              // and can't double the transcript.
              const headSeqRaw = payload.replayHeadSeq;
              const headSeq = typeof headSeqRaw === 'number' ? headSeqRaw : folded.lastEventSeq;
              seenSeqRef.current.set(replaySessionId, headSeq);
              // Apply the folded transcript ONCE. ``hydrated`` resets runState to
              // idle (no spurious inflight) and its ``wouldRegress`` guard makes
              // this the reconcile step against any optimistic localStorage cache
              // already rendered: same/newer server fold applies, a richer cache
              // is preserved.
              dispatch(
                epiChatActions.hydrated({
                  sessionId: replaySessionId,
                  messages: folded.messages,
                  artifacts: folded.artifacts,
                  activeArtifactId: folded.activeArtifactId,
                  activeArtifactTab: folded.activeArtifactTab,
                  lastEventSeq: headSeq,
                })
              );
              return;
            }
            // Fallback (flag-off / no durable ui# stream): the backend
            // synthesised an FE-shape snapshot (messages + artifacts) from prior
            // session state; hand it straight to the ``hydrated`` reducer — the
            // same path localStorage hydration uses.
            const messagesPayload = payload.messages;
            const artifactsPayload = payload.artifacts;
            const messages = Array.isArray(messagesPayload)
              ? (messagesPayload as EpiMessage[])
              : [];
            const artifacts = Array.isArray(artifactsPayload)
              ? (artifactsPayload as EpiArtifact[])
              : [];
            dispatch(
              epiChatActions.hydrated({
                sessionId: replaySessionId,
                messages,
                artifacts,
                activeArtifactId: null,
                activeArtifactTab: null,
              })
            );
            return;
          }
          if (asString(payload.kind) === 'active_dataset') {
            // Legacy /epi-study-agent pin-control echo. The BE emits
            // this on first subscribe (carrying any rehydrated value
            // from session_state.json), on resume, and after every
            // set_active_dataset ack. Project-bound chats never emit
            // it — the slot stays null and the pin-control is hidden
            // on that surface. ``name`` is the fully-qualified
            // EXPORT.<schema> string the BE normalises into, or null
            // when no dataset is pinned.
            const nameRaw = payload.name;
            const name = typeof nameRaw === 'string' && nameRaw.length > 0 ? nameRaw : null;
            dispatch(epiChatActions.activeDatasetChanged({ name }));
            return;
          }
          // OR-6021: BE-driven ``task_scope`` broadcast (renamed from
          // ``focus_scope``). The agent declared it's now working on a
          // specific artifact; we parse the carried scope through the
          // WireScopeSchema at the trust boundary, then store it on the
          // slice. Previously this drove a URL flip via
          // ``useFocusScopeNavigator``; that navigator is now a no-op
          // and the slot is consulted only by an optional chip UI (if
          // shipped) and as a record of the most recent agent-declared
          // scope for observability. Malformed payloads (parse failure)
          // are skipped defensively — a logged warning surfaces FE/BE
          // drift without breaking the session.
          //
          // The legacy ``focus_scope`` kind is still accepted during
          // the BE→FE transition; remove once the BE emit fully flips.
          const eventKind = asString(payload.kind);
          if (eventKind === 'task_scope' || eventKind === 'focus_scope') {
            const parsed = WireScopeSchema.safeParse(payload.scope);
            if (!parsed.success) {
              console.warn(
                '[epi-chat] dropping task_scope event: malformed scope payload',
                parsed.error.issues
              );
              return;
            }
            dispatch(epiChatActions.remoteFocusScopeReceived({ scope: parsed.data }));
            return;
          }
          if (asString(payload.kind) === 'resume' && asString(payload.status) === 'gap') {
            // Backend told us the resume cursor is older than its ring buffer
            // — we've missed more tokens than it can replay. Reset the open
            // streaming message to `error` so the continuation renders cleanly
            // as a new assistant message rather than concatenating onto stale
            // partial prose. Better a visible seam than silently wrong text.
            dispatch(epiChatActions.resumeGapAcknowledged());
            return;
          }
          if (asString(payload.kind) === 'checkpoint') {
            const checkpointId = asString(payload.checkpoint_id) ?? asString(payload.checkpointId);
            const stageRaw = asString(payload.stage);
            const stage = CHECKPOINT_STAGES.includes(stageRaw as EpiCheckpointStage)
              ? (stageRaw as EpiCheckpointStage)
              : null;
            if (!checkpointId || !stage) return;
            const rawApplied = payload.applied_knowledge ?? payload.appliedKnowledge;
            const appliedKnowledge: EpiCheckpoint['appliedKnowledge'] = Array.isArray(rawApplied)
              ? rawApplied
                  .filter(
                    (entry): entry is Record<string, unknown> =>
                      typeof entry === 'object' && entry !== null
                  )
                  .map((entry) => ({
                    id: asString(entry.id) ?? '',
                    kind: asString(entry.kind) ?? '',
                    scope: asString(entry.scope) ?? '',
                    source: asString(entry.source) ?? '',
                    title: asString(entry.title) ?? '',
                    granularity: asString(entry.granularity),
                  }))
                  .filter((entry) => entry.id && entry.title)
              : [];
            // Stage-keyed payload parser — no stage-specific conditionals
            // here; adding a new stage with structured state means adding
            // an entry to ``CHECKPOINT_PAYLOAD_PARSERS`` in
            // ``api/checkpointPayloadParsers``.
            const stagePayload = CHECKPOINT_PAYLOAD_PARSERS[stage](payload.payload);
            // OR-6357 (issue 8): the slim request_approval gate carries the
            // gated asset's identity — the review prompt renders the name from
            // here instead of guessing from sibling cards. Absent on legacy
            // payloads; only the two known kinds are accepted.
            const rawAssetKind = asString(payload.asset_kind) ?? asString(payload.assetKind);
            const assetKind =
              rawAssetKind === 'analytical_variable' ||
              rawAssetKind === 'cohort' ||
              rawAssetKind === 'analysis'
                ? rawAssetKind
                : undefined;
            // OR-6368: create vs edit gate — drives "I've created …" vs
            // "I've updated …" in the review prompt. Absent on legacy payloads.
            const rawAssetOperation =
              asString(payload.asset_operation) ?? asString(payload.assetOperation);
            const assetOperation =
              rawAssetOperation === 'created' ||
              rawAssetOperation === 'updated' ||
              rawAssetOperation === 'mixed'
                ? rawAssetOperation
                : undefined;
            // A combined "Approve all" checkpoint carries member_count > 1
            // (the backend coalesced N create gates). The review prompt renders
            // count-based copy and a single Approve stamps every member. Absent /
            // 1 on a normal single-asset gate.
            const rawMemberCount = payload.member_count ?? payload.memberCount;
            // OR-6504: a family gate also carries member_count, but its copy is
            // different in kind — only ONE variable exists at this point, so the
            // batch line ("I've created N …") would be false. Suppress
            // assetCount when a usable family block is present so the family
            // branch owns the rendering outright.
            const family = asVariableFamily(payload.family);
            const assetCount =
              !family && typeof rawMemberCount === 'number' && rawMemberCount > 1
                ? rawMemberCount
                : undefined;
            const checkpoint: EpiCheckpoint = {
              checkpointId,
              stage,
              summary: asString(payload.summary) ?? '',
              // TODO: cleanup with research_assistant_criterion_redundancy_guard ff
              summaryNotes: asStringArray(payload.summary_notes ?? payload.summaryNotes),
              proposedNext: asString(payload.proposed_next) ?? asString(payload.proposedNext) ?? '',
              appliedKnowledge,
              payload: stagePayload,
              assetKind,
              assetDisplayName:
                asString(payload.asset_display_name) ?? asString(payload.assetDisplayName),
              assetOperation,
              assetCount,
              family,
              answered: false,
            };
            dispatch(epiChatActions.checkpointReceived({ runId, checkpoint }));
            return;
          }
          if (asString(payload.kind) === 'av_selection') {
            const checkpointId = asString(payload.checkpoint_id) ?? asString(payload.checkpointId);
            if (!checkpointId) return;
            const parseCandidate = (raw: unknown): AvSelectionCandidate | null => {
              if (!raw || typeof raw !== 'object') return null;
              const r = raw as Record<string, unknown>;
              const id = asString(r.analyticalVariableId) ?? asString(r.analytical_variable_id);
              const name = asString(r.name);
              if (!id || !name) return null;
              const kindRaw = asString(r.kind);
              return {
                analyticalVariableId: id,
                name,
                dataElement: asString(r.dataElement) ?? asString(r.data_element) ?? name,
                kind: kindRaw === 'de_novo' ? 'de_novo' : 'av_ir',
                reason: asString(r.reason),
              };
            };
            const primary = parseCandidate(payload.primary);
            const alternatives: AvSelectionCandidate[] = Array.isArray(payload.alternatives)
              ? (payload.alternatives as unknown[])
                  .map(parseCandidate)
                  .filter((c): c is AvSelectionCandidate => c !== null)
              : [];
            const checkpoint: AvSelectionCheckpoint = {
              checkpointId,
              summary: asString(payload.summary) ?? '',
              requestedVariable:
                asString(payload.requested_variable) ?? asString(payload.requestedVariable),
              primary,
              alternatives,
              allowBuildNew: payload.allow_build_new !== false,
              // Only the exact literal counts. Absent, null, or anything
              // unrecognized leaves it undefined — i.e. the reuse card — so a
              // malformed payload can never suppress the Build-new affordance.
              purpose:
                asString(payload.purpose) === 'search_reference_variable'
                  ? 'search_reference_variable'
                  : undefined,
              answered: false,
            };
            dispatch(epiChatActions.avSelectionCheckpointReceived({ runId, checkpoint }));
            return;
          }
          if (asString(payload.status) === 'ready') {
            // Backend's "subscribe handshake complete" ack. Clears the
            // pane-level "loading session…" affordance and opens the submit
            // gate, carrying two fields the FE applies atomically: ``welcome``
            // (whether a greeting will stream — false means clear
            // ``initializing`` now, since gen_3 sends no welcome events) and
            // ``inflightRun`` (a request still processing — flip to the Stop
            // state without a frame of Send showing first). Parsed at the trust
            // boundary; a malformed/legacy ack degrades to welcome=false,
            // inflightRun=null.
            const parsed = ReadyAckSchema.safeParse(payload);
            const ack = parsed.success
              ? parsed.data
              : { welcome: false, inflightRun: null, interruptedRun: null };
            dispatch(
              epiChatActions.subscribeReady({
                welcome: ack.welcome,
                inflightRun: ack.inflightRun,
                interruptedRun: ack.interruptedRun,
              })
            );
            return;
          }
          if (asString(payload.status) === 'cancelled') {
            dispatch(epiChatActions.runCancelled());
            if (!isFolding) runLifecycle?.noteRunCancelled(sessionId);
            return;
          }
          // OR-6324/OR-6328 — best-effort tool failure. The backend already
          // logged the raw cause (telemetry WARNING) and persisted this event
          // durably; here we only route the generic plain-language message to
          // the affected tool row. Must run BEFORE the reasoning fall-through
          // below: the payload carries ``message``, which would otherwise
          // render as stray narration (or vanish into the sub-agent internal
          // log, hidden when tool internals are hidden).
          if (asString(payload.status) === 'tool_failure_best_effort') {
            const failedToolId = asString(payload.toolId);
            const failureMessage = asString(payload.message);
            if (failedToolId && failureMessage) {
              dispatch(
                epiChatActions.toolFailureNoted({
                  toolId: failedToolId,
                  message: failureMessage,
                })
              );
            }
            return;
          }
          // Strands reasoning events come through as STATUS_UPDATE with a text
          // body. Two routing cases:
          //   - With a `parentEventId` (sub-agent narration) → append to that
          //     parent tool's internalLog so the drill-down renders the
          //     reasoning interleaved with the tool calls it narrates.
          //     Never lands on the orchestrator's assistant bubble.
          //   - Without a parent (orchestrator's own reasoning) → stream into
          //     the assistant message as italic muted text so the user can
          //     watch the orchestrator's thinking in real time.
          const reasoningText =
            asString(payload.text) ?? asString(payload.message) ?? asString(payload.reasoning);
          if (reasoningText) {
            const parentToolId = event.parentEventId;
            if (parentToolId) {
              dispatch(
                epiChatActions.subAgentReasoningReceived({
                  parentToolId,
                  text: reasoningText,
                  agentId: event.agentId,
                })
              );
            } else {
              dispatch(epiChatActions.reasoningReceived({ runId, text: reasoningText }));
            }
          }
          return;
        }
        case 'ARTIFACT_START': {
          const id = asString(payload.artifactId);
          if (!id) return;
          const rawType = asString(payload.artifactType);
          const artifactType: 'sql' | 'markdown' | 'json' | 'unknown' =
            rawType === 'sql' || rawType === 'markdown' || rawType === 'json' ? rawType : 'unknown';
          // Pull manifest metadata when present. The backend namespace
          // splices these into the ARTIFACT_START payload from
          // `avs/<slug>/manifest.json` so the UI can render an artifact card
          // (kind badge, sources chips, knowledge-applied citations) without
          // a separate fetch round-trip.
          const slug = asString(payload.slug) ?? undefined;
          const kindRaw = asString(payload.kind);
          const kind =
            kindRaw === 'av' ||
            kindRaw === 'av_denovo' ||
            kindRaw === 'composition' ||
            kindRaw === 'concept_group'
              ? kindRaw
              : undefined;
          // New wire key ``format`` (OR-6039) first, legacy ``outputType`` fallback.
          const formatRaw = asString(payload.format) ?? asString(payload.outputType);
          const format =
            formatRaw === 'boolean' ||
            formatRaw === 'numeric' ||
            formatRaw === 'string' ||
            formatRaw === 'date'
              ? formatRaw
              : undefined;
          const outputValueTypeRaw = asString(payload.outputValueType);
          const outputValueType =
            outputValueTypeRaw === 'boolean' ||
            outputValueTypeRaw === 'signed_integer' ||
            outputValueTypeRaw === 'unsigned_integer' ||
            outputValueTypeRaw === 'unsigned_decimal' ||
            outputValueTypeRaw === 'signed_decimal' ||
            outputValueTypeRaw === 'string' ||
            outputValueTypeRaw === 'date'
              ? outputValueTypeRaw
              : undefined;
          const outputValues = asString(payload.outputValues) ?? undefined;
          const statusRaw = asString(payload.status);
          const artifactStatus =
            statusRaw === 'drafting' || statusRaw === 'validated' || statusRaw === 'blocked'
              ? statusRaw
              : undefined;
          const description = asString(payload.description) ?? undefined;
          const explainMarkdown =
            asString(payload.explainMarkdown) ?? asString(payload.explain_markdown) ?? undefined;
          // Structured clinical-explanation object is the source of truth
          // for the Description tab. Each section is optional; the
          // renderer drops any whose body is empty. Wire shape mirrors
          // ``ClinicalExplanation`` on the backend manifest with field
          // names converted to camelCase by the namespace's manifest_meta
          // builder. Unknown fields ignored — defensive against a manifest
          // schema that grows on the backend before the frontend updates.
          const clinicalExplanationRaw = asRecord(payload.clinicalExplanation);
          const clinicalExplanation = clinicalExplanationRaw
            ? {
                answers: asString(clinicalExplanationRaw.answers),
                value: asString(clinicalExplanationRaw.value),
                qualifyingCriteria: asString(clinicalExplanationRaw.qualifyingCriteria),
                timeWindow: asString(clinicalExplanationRaw.timeWindow),
                valueSelection: asString(clinicalExplanationRaw.valueSelection),
                missingValues: asString(clinicalExplanationRaw.missingValues),
                outOfScope: asString(clinicalExplanationRaw.outOfScope),
              }
            : undefined;
          const coreLabel = asString(payload.coreLabel) ?? undefined;
          const sources = Array.isArray(payload.sources)
            ? (payload.sources as unknown[]).filter((v): v is string => typeof v === 'string')
            : undefined;
          const conceptGroupIds = Array.isArray(payload.conceptGroupIds)
            ? (payload.conceptGroupIds as unknown[]).filter(
                (v): v is string => typeof v === 'string'
              )
            : undefined;
          const knowledgeAppliedRaw = payload.knowledgeApplied;
          const knowledgeApplied = Array.isArray(knowledgeAppliedRaw)
            ? knowledgeAppliedRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => ({
                  id: asString(entry.id) ?? '',
                  kind: asString(entry.kind) ?? '',
                  scope: asString(entry.scope) ?? '',
                  source: asString(entry.source) ?? '',
                  title: asString(entry.title) ?? '',
                  granularity: asString(entry.granularity),
                }))
                .filter((entry) => entry.id && entry.title)
            : undefined;
          // Concept bindings — drafter-time decision log, surfaced on the
          // Concepts tab. Each entry is the resolved concept group + the
          // rationale + alternatives the agent considered. Backend sends
          // these on ARTIFACT_START as part of the manifest; the wire
          // shape is camelCase already (manifest_meta in the namespace).
          const conceptBindingsRaw = payload.conceptBindings;
          const conceptBindings = Array.isArray(conceptBindingsRaw)
            ? conceptBindingsRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => {
                  const statusRaw = asString(entry.status);
                  // 'selected' (or legacy 'curated') and any unknown value
                  // normalise to 'selected'; 'proposed'/'inline' pass through.
                  const status: EpiConceptBinding['status'] =
                    statusRaw === 'proposed' || statusRaw === 'inline' ? statusRaw : 'selected';
                  const altsRaw = entry.alternativesConsidered;
                  const alternativesConsidered = Array.isArray(altsRaw)
                    ? (altsRaw as unknown[]).filter((v): v is string => typeof v === 'string')
                    : undefined;
                  return {
                    conceptGroupId: asString(entry.conceptGroupId) ?? '',
                    conceptGroupName: asString(entry.conceptGroupName) ?? '',
                    status,
                    rationale: asString(entry.rationale),
                    alternativesConsidered,
                  };
                })
                .filter(
                  (entry) =>
                    entry.conceptGroupId && (entry.conceptGroupName || entry.conceptGroupId)
                )
            : undefined;
          // Structured term→concept mappings (OR-6214) — rendered read-only on
          // the Definition tab. Backend splices these into the ARTIFACT_START
          // manifest_meta; the shared parser tolerates camelCase (this surface)
          // and snake_case (the checkpoint payload) alike. Empty → undefined so
          // the section hides.
          const conceptMappingsParsed = parseConceptMappings(
            payload.conceptMappings ?? payload.concept_mappings
          );
          const conceptMappings =
            conceptMappingsParsed.length > 0 ? conceptMappingsParsed : undefined;
          // Design decisions — drafter-time {decision, rationale} rows.
          // Surfaced on the Decisions tab as the durable record of why the
          // SQL is shaped the way it is. Wire shape is already camelCase.
          const designDecisionsRaw = payload.designDecisions;
          const designDecisions = Array.isArray(designDecisionsRaw)
            ? designDecisionsRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => ({
                  decision: asString(entry.decision) ?? '',
                  rationale: asString(entry.rationale) ?? '',
                }))
                .filter((entry) => entry.decision && entry.rationale)
            : undefined;
          const validationBlocking =
            typeof payload.validationBlocking === 'boolean'
              ? payload.validationBlocking
              : undefined;
          // Sharing scope from the analytical variable's KNOWLEDGE-store registration
          // (looked up server-side at emit time). When the analytical variable hasn't been
          // registered yet — typical during the streaming chunks of a
          // freshly-finalized artifact — `scope` is absent and the UI
          // surfaces a "Still being worked on" pill instead of a chip.
          const scopeRaw = asString(payload.scope);
          const scope =
            scopeRaw === 'session' || scopeRaw === 'user' || scopeRaw === 'org'
              ? scopeRaw
              : undefined;
          const latestVersionId = asString(payload.latestVersionId) ?? undefined;
          // Concept-group manifest fields. `codes` is a flat list; `definition`
          // is set only for rule-based groups (the Definition tab is
          // gated on its presence). `usage` is the cross-study consumer
          // list — typically populated lazily, so often absent here.
          const codesRaw = payload.codes;
          const codes: EpiConceptGroupCode[] | undefined = Array.isArray(codesRaw)
            ? codesRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => ({
                  code: asString(entry.code) ?? '',
                  vocabulary: asString(entry.vocabulary) ?? '',
                  description: asString(entry.description),
                  conceptId: asString(entry.conceptId),
                  isExcluded: typeof entry.isExcluded === 'boolean' ? entry.isExcluded : undefined,
                }))
                .filter((entry) => entry.code && entry.vocabulary)
            : undefined;
          const definitionRaw = asRecord(payload.definition);
          const definitionLanguage = asString(definitionRaw.language);
          const definitionBody = asString(definitionRaw.body);
          const definition: EpiConceptGroupDefinition | undefined =
            (definitionLanguage === 'sql' || definitionLanguage === 'markdown') && definitionBody
              ? { language: definitionLanguage, body: definitionBody }
              : undefined;
          const usageRaw = payload.usage;
          const usage: EpiConceptGroupUsage[] | undefined = Array.isArray(usageRaw)
            ? usageRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry): EpiConceptGroupUsage => {
                  const consumerKindRaw = asString(entry.consumerKind);
                  const consumerKind: EpiConceptGroupUsage['consumerKind'] =
                    consumerKindRaw === 'cohort' ? 'cohort' : 'av';
                  return {
                    consumerId: asString(entry.consumerId) ?? '',
                    consumerKind,
                    consumerLabel: asString(entry.consumerLabel) ?? '',
                    studyId: asString(entry.studyId),
                    studyLabel: asString(entry.studyLabel),
                  };
                })
                .filter((entry) => entry.consumerId && entry.consumerLabel)
            : undefined;
          dispatch(
            epiChatActions.artifactStarted({
              id,
              artifactType,
              // Display label: new wire ``dataElement`` first, legacy ``name``
              // fallback, finally the id so the card always has a title.
              dataElement: asString(payload.dataElement) ?? asString(payload.name) ?? id,
              // The artifact id (the slug value) rides the unchanged wire ``slug`` key.
              name: slug,
              kind,
              description,
              explainMarkdown,
              clinicalExplanation,
              coreLabel,
              outputValueType,
              outputValues,
              format,
              conceptGroupIds,
              sources,
              knowledgeApplied,
              conceptBindings,
              conceptMappings,
              designDecisions,
              artifactStatus,
              validationBlocking,
              scope,
              latestVersionId,
              codes,
              definition,
              usage,
            })
          );
          return;
        }
        case 'ARTIFACT_EXECUTION_START': {
          const artifactId = asString(payload.artifactId);
          const executionId = asString(payload.executionId);
          const datasetReleaseId = asString(payload.datasetReleaseId);
          if (!artifactId || !executionId || !datasetReleaseId) return;
          dispatch(
            epiChatActions.executionStarted({
              artifactId,
              executionId,
              versionId: asString(payload.versionId),
              datasetReleaseId,
              datasetReleaseDisplayName: asString(payload.datasetReleaseDisplayName),
              userId: asString(payload.userId),
              userName: asString(payload.userName),
              startedAt: asString(payload.startedAt),
              studyStart: asString(payload.studyStart),
              studyEnd: asString(payload.studyEnd),
              indexAvSlug: asString(payload.indexAvSlug),
              indexCalendarDate: asString(payload.indexCalendarDate),
            })
          );
          return;
        }
        case 'ARTIFACT_EXECUTION_PARTIAL': {
          // Mid-flight section update on a still-pending execution. Carries
          // any subset of the four epi sections (coverage / distribution /
          // conceptUsage / technicalChecks). The reducer merges sections
          // non-destructively — a partial that only carries technicalChecks
          // does NOT clear an already-set coverage. Status stays `pending`
          // until ARTIFACT_EXECUTION_END fires; same shape parsing as END.
          const artifactId = asString(payload.artifactId);
          const executionId = asString(payload.executionId);
          if (!artifactId || !executionId) return;
          const coverage =
            payload.coverage && typeof payload.coverage === 'object'
              ? (payload.coverage as EpiExecutionCoverage)
              : undefined;
          const distribution =
            payload.distribution && typeof payload.distribution === 'object'
              ? (payload.distribution as EpiExecutionDistribution)
              : undefined;
          const conceptUsage = Array.isArray(payload.conceptUsage)
            ? (payload.conceptUsage as EpiExecutionConceptUsage[])
            : undefined;
          const technicalChecks =
            payload.technicalChecks && typeof payload.technicalChecks === 'object'
              ? (payload.technicalChecks as EpiExecutionTechnicalChecks)
              : undefined;
          const attrition = parseAttritionPayload(payload.attrition);
          dispatch(
            epiChatActions.executionPartialReceived({
              artifactId,
              executionId,
              coverage,
              distribution,
              conceptUsage,
              technicalChecks,
              attrition,
            })
          );
          return;
        }
        case 'ARTIFACT_EXECUTION_LOG': {
          const artifactId = asString(payload.artifactId);
          const executionId = asString(payload.executionId);
          const content = asString(payload.content);
          if (!artifactId || !executionId || !content) return;
          dispatch(epiChatActions.executionLogChunkReceived({ artifactId, executionId, content }));
          return;
        }
        case 'ARTIFACT_EXECUTION_END': {
          const artifactId = asString(payload.artifactId);
          const executionId = asString(payload.executionId);
          const statusRaw = asString(payload.status);
          if (!artifactId || !executionId) return;
          const status =
            statusRaw === 'passed' ||
            statusRaw === 'failed' ||
            statusRaw === 'error' ||
            statusRaw === 'empty'
              ? statusRaw
              : 'error';
          const computeModeRaw = asString(payload.computeMode);
          const computeMode =
            computeModeRaw === 'fresh' || computeModeRaw === 'cached' ? computeModeRaw : undefined;
          const errorsRaw = payload.errors;
          const errors = Array.isArray(errorsRaw)
            ? errorsRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => ({
                  message: asString(entry.message) ?? '',
                  line: typeof entry.line === 'number' ? entry.line : undefined,
                }))
                .filter((entry) => entry.message)
            : undefined;
          // The four epi sections: passed through as opaque shapes from the
          // backend. Validation that they conform to the type is the
          // backend's job — keeping the reducer light avoids a giant
          // schema-validation surface in the hot path. If the server emits
          // garbage the renderer will simply skip rendering that section.
          const coverage =
            payload.coverage && typeof payload.coverage === 'object'
              ? (payload.coverage as EpiExecutionCoverage)
              : undefined;
          const distribution =
            payload.distribution && typeof payload.distribution === 'object'
              ? (payload.distribution as EpiExecutionDistribution)
              : undefined;
          const conceptUsage = Array.isArray(payload.conceptUsage)
            ? (payload.conceptUsage as EpiExecutionConceptUsage[])
            : undefined;
          const technicalChecks =
            payload.technicalChecks && typeof payload.technicalChecks === 'object'
              ? (payload.technicalChecks as EpiExecutionTechnicalChecks)
              : undefined;
          const attrition = parseAttritionPayload(payload.attrition);
          dispatch(
            epiChatActions.executionEnded({
              artifactId,
              executionId,
              status,
              summary: asString(payload.summary),
              resultHash: asString(payload.resultHash),
              computeMode,
              coverage,
              distribution,
              conceptUsage,
              technicalChecks,
              attrition,
              errors,
            })
          );
          return;
        }
        case 'ARTIFACT_RESULTS_START': {
          const artifactId = asString(payload.artifactId);
          const resultId = asString(payload.resultId);
          if (!artifactId || !resultId) return;
          const columns = Array.isArray(payload.columns)
            ? (payload.columns as unknown[]).filter((v): v is string => typeof v === 'string')
            : [];
          const summaryStatsRaw = payload.summaryStats;
          const summaryStats = Array.isArray(summaryStatsRaw)
            ? summaryStatsRaw
                .filter(
                  (entry): entry is Record<string, unknown> =>
                    typeof entry === 'object' && entry !== null
                )
                .map((entry) => ({
                  label: asString(entry.label) ?? '',
                  value: asString(entry.value) ?? '',
                }))
                .filter((entry) => entry.label)
            : undefined;
          dispatch(
            epiChatActions.resultsStarted({
              artifactId,
              resultId,
              columns,
              rowCount: typeof payload.rowCount === 'number' ? payload.rowCount : undefined,
              summaryStats,
              computedAt: asString(payload.computedAt),
            })
          );
          return;
        }
        case 'ARTIFACT_RESULTS_ROWS': {
          const artifactId = asString(payload.artifactId);
          const resultId = asString(payload.resultId);
          if (!artifactId || !resultId) return;
          const rowsRaw = payload.rows;
          if (!Array.isArray(rowsRaw)) return;
          // Each cell is string | number | null. Any other JSON type is
          // coerced to string so the grid never has to render `[object Object]`.
          const rows = rowsRaw
            .filter((row): row is unknown[] => Array.isArray(row))
            .map((row) =>
              row.map((cell) => {
                if (cell === null) return null;
                if (typeof cell === 'string' || typeof cell === 'number') return cell;
                return JSON.stringify(cell);
              })
            );
          dispatch(epiChatActions.resultsRowsAppended({ artifactId, resultId, rows }));
          return;
        }
        case 'ARTIFACT_RESULTS_END': {
          const artifactId = asString(payload.artifactId);
          const resultId = asString(payload.resultId);
          if (!artifactId || !resultId) return;
          dispatch(
            epiChatActions.resultsCompleted({
              artifactId,
              resultId,
              rowCount: typeof payload.rowCount === 'number' ? payload.rowCount : undefined,
            })
          );
          return;
        }
        case 'ARTIFACT_CHUNK': {
          const id = asString(payload.artifactId);
          const content = asString(payload.content);
          if (!id || !content) return;
          dispatch(epiChatActions.artifactChunkReceived({ id, content }));
          return;
        }
        case 'ARTIFACT_END': {
          const id = asString(payload.artifactId);
          if (!id) return;
          dispatch(epiChatActions.artifactCompleted({ id }));
          return;
        }
        case 'RESOURCE_CARD': {
          // OR-6089: add or refresh a project-resource chat card.
          // Trust-boundary parse — drop malformed payloads with a logged
          // warning rather than throwing, mirroring how `task_scope`
          // handles `WireScopeSchema.safeParse` failures.
          const parsed = ResourceCardPayloadSchema.safeParse(payload);
          if (!parsed.success) {
            console.warn(
              '[epi-chat] dropping RESOURCE_CARD: malformed payload',
              parsed.error.issues
            );
            return;
          }
          const now = new Date().toISOString();
          dispatch(
            epiChatActions.resourceCardReceived({
              ...parsed.data,
              createdAt: now,
              updatedAt: now,
            })
          );
          return;
        }
        case 'UPLOADED_FILE': {
          // OR-6125: a distinct uploaded-file chip the user can re-download.
          // Trust-boundary parse — drop malformed payloads with a logged
          // warning rather than throwing (mirrors RESOURCE_CARD).
          const parsed = UploadedFilePayloadSchema.safeParse(payload);
          if (!parsed.success) {
            console.warn(
              '[epi-chat] dropping UPLOADED_FILE: malformed payload',
              parsed.error.issues
            );
            return;
          }
          dispatch(
            epiChatActions.uploadedFileReceived({
              fileName: parsed.data.fileName,
              uploadId: parsed.data.uploadId,
            })
          );
          return;
        }
        case 'RESOURCE_UPDATED': {
          // OR-6089: server-state-changed signal. Invalidate the
          // kind-specific query keys so the TanStack-driven detail /
          // list pages refetch in place. No reducer state mutation
          // here — the source of truth is server state the page
          // refetches, not anything carried in the slice.
          // RESOURCE_UPDATED is a pure side-effect signal (TanStack
          // invalidations + cohort-poll kickoff) — it mutates NO transcript
          // state. During a replay fold it's a no-op: re-running a historical
          // "cohort PROCESSING" would otherwise restart a live status poll.
          if (isFolding) return;
          const parsed = ResourceUpdatedPayloadSchema.safeParse(payload);
          if (!parsed.success) {
            console.warn(
              '[epi-chat] dropping RESOURCE_UPDATED: malformed payload',
              parsed.error.issues
            );
            return;
          }
          const {
            resourceKind,
            resourceId,
            projectId,
            parentIds,
            optimisticPatch,
            newStatus,
            refreshAssetStaleness,
          } = parsed.data;
          // OR-6147: for ANY cohort change driven by the agent, flag the
          // cohort-builder store so its explicit-save hydration re-seeds from the
          // server refetch even over local staged edits. Set before the
          // invalidate so the flag is in place when the refetched data arrives.
          // Covers mutations the optimistic patch doesn't model (e.g. setting an
          // index date adds a new criterion, not a stub swap).
          if (resourceKind === 'cohort') {
            resourceResolvers.markCohortServerChange?.();
          }
          // OR-6158: a cohort run just started — flip the store into PROCESSING
          // so the detail header updates and the status poll starts (the
          // invalidation below can't move the header on its own).
          if (
            resourceKind === 'cohort' &&
            newStatus === 'PROCESSING' &&
            resourceResolvers.onCohortProcessingStarted
          ) {
            resourceResolvers.onCohortProcessingStarted(parentIds?.cohortVersionId);
          }
          // OR-6089: apply the optimistic patch BEFORE the invalidate
          // fires its TQ refetch, so the criteria card flips
          // immediately. The server-confirmed refetch arrives a few
          // hundred ms later and reconciles (idempotent if the bind
          // matches; reverts on the rare mismatch).
          if (
            optimisticPatch &&
            resourceKind === 'cohort' &&
            resourceResolvers.applyCohortOptimisticPatch
          ) {
            // An optimistic-patch failure must NEVER suppress the
            // server-confirmed invalidation below — that refetch is the source
            // of truth. A throw here previously aborted the handler before
            // invalidateQueries ran, leaving the cohort card stale until a
            // manual refresh. (OR-6177)
            try {
              resourceResolvers.applyCohortOptimisticPatch({
                ...optimisticPatch,
                cohortVersionId: parentIds?.cohortVersionId,
              });
            } catch (err) {
              console.warn(
                '[epi-chat] applyCohortOptimisticPatch failed; relying on invalidation',
                err
              );
            }
          }
          // The RA's process_analysis tool started a run: write PROCESSING onto the
          // cached profile so the chat card and detail header flip and the status
          // poll starts, and skip the profile refetch that could cache a stale
          // pre-PROCESSING snapshot.
          // TODO: cleanup with descriptive_analysis_impl ff
          const isAnalysisRunStarted = resourceKind === 'analysis' && newStatus === 'PROCESSING';
          if (isAnalysisRunStarted) {
            markAnalysisProcessing(queryClient, resourceId);
          }
          const analysisProfileKey = JSON.stringify(analysisKeys.profile(resourceId));
          for (const queryKey of resourceInvalidationKeys(
            resourceKind,
            resourceId,
            {
              projectId,
              parentIds,
              optimisticPatchKind: optimisticPatch?.kind,
              refreshAssetStaleness,
              scopedAvInvalidation,
            },
            resourceResolvers
          )) {
            if (isAnalysisRunStarted && JSON.stringify(queryKey) === analysisProfileKey) continue;
            queryClient.invalidateQueries({ queryKey });
          }
          // TODO: cleanup with scoped_av_card_invalidation ff
          if (resourceKind === 'analytical_variable' && scopedAvInvalidation) {
            queryClient.invalidateQueries({ predicate: isAvLibraryListQuery });
            queryClient.invalidateQueries({ predicate: isAvDetailQueryForVariable(resourceId) });
          }
          return;
        }
        case 'WARNING': {
          // Render backend WARNINGs (e.g. the silent-run stall nudge
          // RUN_STALLED_USER_PROMPT) as a non-blocking inline notice instead of
          // dropping them. Tool-step failures are no longer surfaced here — they
          // live in the collapsed working-state row, and an unrecoverable failure
          // resolves that row to an actionable error (OR-6151 / RA response
          // structure).
          //
          // SYSTEM_ACK is the concept-group create→resume acknowledgment ("You
          // created … — continuing the build"). The BE still emits it (it drives
          // the resume), but we don't render an ack box for it in chat — the
          // agent's own reply already acknowledges the save. Drop it silently.
          if (asString(payload.code) === 'SYSTEM_ACK') {
            return;
          }
          const message = asString(payload.message);
          if (message) {
            // Severity rides the payload so the same channel carries calm
            // ``info`` notices (OR-6247 A8 create→resume SYSTEM acknowledgment)
            // as well as ``warning`` nudges; default to ``warning`` for legacy
            // WARNINGs that don't set it.
            const rawSeverity = asString(payload.severity);
            const severity =
              rawSeverity === 'info' || rawSeverity === 'error' ? rawSeverity : 'warning';
            dispatch(epiChatActions.noticeReceived({ message, severity }));
          }
          return;
        }
        case 'CONNECTION_ESTABLISHED':
        case 'METADATA':
          return;
        default:
          return;
      }
    },
    [
      dispatchTo,
      activeSessionIdRef,
      queryClient,
      resourceResolvers,
      scopedAvInvalidation,
      runLifecycle,
    ]
  );
  handlerRef.current = handleEvent;
  return handleEvent;
};
